import { Redirect, usePathname } from 'expo-router';
import { observer } from 'mobx-react-lite';
import type { PropsWithChildren } from 'react';

import { HydrationSplash } from '@/components/HydrationSplash';
import { authStore } from '@/stores/auth.store';

/**
 * Shared gate for every auth-required route group. Encodes the two
 * rules each layout was repeating by hand:
 *
 * 1. Never redirect before authStore.hydrate() resolves — a cold web
 *    load at a deep link looks logged-out on the first frame, and
 *    redirecting then bounces an authed user to login and loses the
 *    URL. Render HydrationSplash instead.
 * 2. When genuinely logged out, redirect to login carrying the
 *    intended path (?redirect=) so the PIN flow can return the user
 *    to where they were headed.
 *
 * Wrap the layout's content: <AuthGate><Stack ... /></AuthGate>.
 */
export const AuthGate = observer(({ children }: PropsWithChildren) => {
  const pathname = usePathname();

  if (!authStore.hydrated) {
    return <HydrationSplash />;
  }
  if (!authStore.isAuthenticated) {
    return <Redirect href={{ pathname: '/(auth)/login', params: { redirect: pathname } }} />;
  }
  return <>{children}</>;
});
